import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CalendarClock, ChevronDown, ChevronUp } from "lucide-react";
import { fmtDate } from "@/lib/format";

/** Convert HH:MM (or empty) to minutes-since-midnight, or null. */
function toMin(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}
/** True if two time windows overlap. Blank fields ⇒ treat as full-day. */
function overlaps(aStart, aEnd, bStart, bEnd) {
  const As = toMin(aStart) ?? 0;
  const Ae = toMin(aEnd) ?? 24 * 60;
  const Bs = toMin(bStart) ?? 0;
  const Be = toMin(bEnd) ?? 24 * 60;
  return As < Be && Bs < Ae;
}

export default function RehearsalConflictPreview({ date, startTime, endTime }) {
  const [conflicts, setConflicts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/conflicts");
        setConflicts(data);
      } catch { /* ignore — component fails silently */ }
      finally { setLoaded(true); }
    })();
  }, []);

  const { dayConflicts, overlapping, otherOnDay } = useMemo(() => {
    if (!date) return { dayConflicts: [], overlapping: [], otherOnDay: [] };
    const dc = conflicts.filter((c) => c.date === date && c.status !== "rejected");
    const ov = dc.filter((c) => overlaps(startTime, endTime, c.start_time, c.end_time));
    const other = dc.filter((c) => !ov.includes(c));
    return { dayConflicts: dc, overlapping: ov, otherOnDay: other };
  }, [conflicts, date, startTime, endTime]);

  if (!loaded || !date) return null;
  if (dayConflicts.length === 0) {
    return (
      <div className="rounded-md border border-emerald-500/25 bg-emerald-500/5 text-emerald-300 text-[11px] px-3 py-2 inline-flex items-center gap-2" data-testid="conflict-preview-clear">
        <CalendarClock className="w-3.5 h-3.5" /> No cast conflicts submitted for {fmtDate(date)}.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="conflict-preview">
      {overlapping.length > 0 && (
        <div className="px-3 py-2 border-b border-red-500/30 bg-red-500/10 flex items-center gap-2 text-red-300" data-testid="conflict-preview-banner">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <div className="text-sm font-medium">
            {overlapping.length} cast conflict{overlapping.length === 1 ? "" : "s"} overlap this rehearsal time.
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-zinc-300 hover:bg-white/5"
        data-testid="conflict-preview-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          <CalendarClock className="w-3.5 h-3.5 text-zinc-400" />
          <span className="uppercase tracking-widest text-[10px] text-zinc-500">On {fmtDate(date)}</span>
          <span className="text-zinc-200">{dayConflicts.length} conflict{dayConflicts.length === 1 ? "" : "s"}</span>
          {overlapping.length > 0 && <span className="text-red-400">· {overlapping.length} overlap this call</span>}
        </span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {expanded && (
        <ul className="divide-y divide-white/5">
          {[...overlapping, ...otherOnDay].map((c) => {
            const isOverlap = overlapping.includes(c);
            return (
              <li
                key={c.id}
                data-testid={`conflict-row-${c.id}`}
                className={`px-3 py-2 flex items-start gap-3 text-xs ${isOverlap ? "bg-red-500/5" : ""}`}
              >
                <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${isOverlap ? "text-red-400" : "text-zinc-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-100 truncate">{c.user_name}</div>
                  <div className="text-zinc-500">
                    <span className="font-mono">{c.start_time}–{c.end_time}</span>
                    {c.reason && <span> — {c.reason}</span>}
                    {c.status === "pending" && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-400">Pending review</span>}
                  </div>
                </div>
                {isOverlap && <span className="text-[10px] uppercase tracking-widest text-red-400 shrink-0">Overlap</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
